"use client"

import type React from "react"
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  // Removed Button import as it's no longer used here
} from "@mui/material"
import { AccountBalance } from "@mui/icons-material" // Removed Download, Print as they are no longer used here

interface Transaction {
  txnDate: string
  voucher: string
  narration: string
  debit: number
  credit: number
  balance: number
}

interface ROIHistory {
  sno: number
  startDate: string
  endDate: string
  roi: string
  emiAmount: number
}

interface LoanStatementData {
  borrowerName: string
  coApplicantName: string
  address: string
  loanNumber: string
  disbursedAmount: number
  termOfLoan: number
  termUnit: string
  rateOfInterest: number
  emiStartDate: string
  periodFrom: string
  periodTo: string
  transactions: Transaction[]
  roiHistory: ROIHistory[]
  pageInfo: {
    currentPage: number
    totalPages: number
    takenBy: string
    dateTime: string
  }
}

interface LoanStatementProps {
  data: LoanStatementData
  // Removed onExportPDF and onPrint props
}

const LoanStatement: React.FC<LoanStatementProps> = ({ data }) => {
  // Format number with Indian numbering system
  const formatIndianNumber = (num: number): string => {
    if (num === 0) return "0.00"
    const isNegative = num < 0
    const absNum = Math.abs(num)
    const formatted = absNum.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
    return isNegative ? `-${formatted}` : formatted
  }

  const InfoRow = ({ label, value, isAmount = false }: any) => (
    <Box className="flex justify-between py-2 px-3 border border-gray-300 rounded mb-2 bg-gray-50">
      <Typography variant="body2" className="text-gray-600 font-semibold text-sm uppercase tracking-wide">
        {label}
      </Typography>
      <Typography variant="body2" className={`font-bold text-sm ${isAmount ? "text-blue-700" : "text-gray-900"} bg-white px-2 py-1 rounded border`}>
        {isAmount && typeof value === "number" ? `$${formatIndianNumber(value)}` : value}
      </Typography>
    </Box>
  )

  const filteredTransactions = data.transactions.filter((t) => !/accrual/i.test(t.narration || ''))

  return (
    <Box className="bg-white">
      {/* Removed Export Actions section from here */}

      <Box className="p-4">
        {/* Header - Compact */}
        <Box sx={{ textAlign: 'center', mb: 4, borderBottom: '2px solid #2563eb', pb: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 2 }}>
            <img 
              src="/logo.png" 
              alt="SMEDCO Logo" 
              style={{ width: 'auto', height: 80, display: 'block', margin: '0 auto' }}
              onError={(e) => {
                // Fallback to icon if logo fails to load
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                const fallback = target.nextElementSibling as HTMLElement;
                if (fallback) fallback.style.display = 'flex';
              }}
            />
            <Box sx={{ width: 40, height: 40, backgroundColor: '#2563eb', borderRadius: '9999px', display: 'none', alignItems: 'center', justifyContent: 'center' }}>
              <AccountBalance style={{ color: '#fff', fontSize: 18 }} />
            </Box>
            {/* Title removed as requested */}
          </Box>
          <Typography variant="h5" className="font-bold text-gray-800 mb-1" sx={{ textAlign: 'center' }}>
            LOAN STATEMENT
          </Typography>
          <Typography variant="body1" className="text-gray-600" sx={{ textAlign: 'center' }}>
            Period: {data.periodFrom} to {data.periodTo}
          </Typography>
        </Box>

        {/* Client Information Section - Single Card */}
        <Card className="shadow-lg border-2 border-gray-300 mb-4">
          <CardContent className="p-4">
            <Typography
              variant="h6"
              className="font-bold text-gray-800 mb-4 text-center bg-gradient-to-r from-blue-500 to-blue-700 text-white py-3 rounded-lg shadow-md"
            >
              📋 LOAN INFORMATION SUMMARY
            </Typography>
            
            <Grid container spacing={3}>
              {/* Borrower Information */}
              <Grid item xs={12} md={6}>
                <Box className="border-2 border-blue-300 rounded-lg p-3 bg-blue-50">
                  <Typography variant="subtitle2" className="font-bold text-blue-800 text-center mb-3 bg-blue-200 py-2 rounded border-b-2 border-blue-400">
                    👤 BORROWER INFORMATION
                  </Typography>
                  <Box className="space-y-2">
                    <InfoRow label="Full Name" value={`Mr/Mrs. ${data.borrowerName}`} />
                    <InfoRow label="Co-Applicant" value={`Mr/Mrs. ${data.coApplicantName || "N/A"}`} />
                    <InfoRow label="Address" value={data.address} />
                    <InfoRow label="Loan Number" value={data.loanNumber} />
                  </Box>
                </Box>
              </Grid>

              {/* Loan Details */}
              <Grid item xs={12} md={6}>
                <Box className="border-2 border-green-300 rounded-lg p-3 bg-green-50">
                  <Typography variant="subtitle2" className="font-bold text-green-800 text-center mb-3 bg-green-200 py-2 rounded border-b-2 border-green-400">
                    💰 LOAN DETAILS
                  </Typography>
                  <Box className="space-y-2">
                    <InfoRow label="Disbursed Amount" value={data.disbursedAmount} isAmount />
                    <InfoRow label="Term of Loan" value={`${data.termOfLoan} ${data.termUnit}`} />
                    <InfoRow label="Rate of Interest" value={`${data.rateOfInterest}% Per Annum`} />
                  </Box>
                </Box>
              </Grid>


            </Grid>
          </CardContent>
        </Card>

        {/* Transaction History - Compact */}
        <Card className="shadow-sm border mb-3">
          <CardContent className="p-3">
            <Typography
              variant="subtitle1"
              className="font-bold text-gray-800 mb-2 text-center bg-gray-100 py-1 rounded text-sm"
            >
              TRANSACTION HISTORY
            </Typography>
            <TableContainer component={Paper} className="shadow-none border">
              <Table size="small">
                <TableHead>
                  <TableRow className="bg-gray-100">
                    <TableCell className="font-bold border text-xs p-1">Date</TableCell>
                    <TableCell className="font-bold border text-xs p-1">Reference</TableCell>
                    <TableCell className="font-bold border text-xs p-1">Description</TableCell>
                    <TableCell className="font-bold border text-xs p-1 text-right">Debit</TableCell>
                    <TableCell className="font-bold border text-xs p-1 text-right">Credit</TableCell>
                    <TableCell className="font-bold border text-xs p-1 text-right">Balance</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredTransactions.slice(0, 15).map((transaction, index) => (
                    <TableRow key={index} className={index % 2 === 0 ? "bg-gray-25" : "bg-white"}>
                      <TableCell className="border text-xs p-1">{transaction.txnDate}</TableCell>
                      <TableCell className="border text-xs p-1">{transaction.voucher}</TableCell>
                      <TableCell className="border text-xs p-1 max-w-xs truncate">{transaction.narration}</TableCell>
                      <TableCell className="border text-xs p-1 text-right">
                        {transaction.debit > 0 ? formatIndianNumber(transaction.debit) : "-"}
                      </TableCell>
                      <TableCell className="border text-xs p-1 text-right">
                        {transaction.credit > 0 ? formatIndianNumber(transaction.credit) : "-"}
                      </TableCell>
                      <TableCell className="border text-xs p-1 text-right font-semibold">
                        {formatIndianNumber(transaction.balance)}
                      </TableCell>
                    </TableRow>
                  ))}

                </TableBody>
              </Table>
            </TableContainer>
            {data.transactions.length > 15 && (
              <Typography variant="body2" className="text-center text-gray-600 mt-2 text-xs">
                Showing first 15 transactions (excluding accruals). Full list available in Excel export.
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* ROI History - Compact */}
        <Card className="shadow-sm border mb-3">
          <CardContent className="p-3">
            <Typography
              variant="subtitle1"
              className="font-bold text-gray-800 mb-2 text-center bg-purple-100 py-1 rounded text-sm"
            >
              RATE OF INTEREST HISTORY
            </Typography>
            <TableContainer component={Paper} className="shadow-none border">
              <Table size="small">
                <TableHead>
                  <TableRow className="bg-gray-100">
                    <TableCell className="font-bold border text-xs p-1">S.No</TableCell>
                    <TableCell className="font-bold border text-xs p-1">Start Date</TableCell>
                    <TableCell className="font-bold border text-xs p-1">End Date</TableCell>
                    <TableCell className="font-bold border text-xs p-1">ROI</TableCell>
                    <TableCell className="font-bold border text-xs p-1 text-right">EMI Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.roiHistory.map((roi, index) => (
                    <TableRow key={index} className={index % 2 === 0 ? "bg-gray-25" : "bg-white"}>
                      <TableCell className="border text-xs p-1">{roi.sno}</TableCell>
                      <TableCell className="border text-xs p-1">{roi.startDate}</TableCell>
                      <TableCell className="border text-xs p-1">{roi.endDate}</TableCell>
                      <TableCell className="border text-xs p-1">{roi.roi}</TableCell>
                      <TableCell className="border text-xs p-1 text-right">
                        {formatIndianNumber(roi.emiAmount)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* Footer - Compact */}
        <Box className="border-t border-gray-300 pt-2">
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Typography variant="body2" className="text-gray-600 text-xs">
                <strong>Total Pages:</strong> {data.pageInfo.totalPages} | <strong>Generated By:</strong>{" "}
                {data.pageInfo.takenBy}
              </Typography>
              <Typography variant="body2" className="text-gray-600 text-xs">
                <strong>Date & Time:</strong> {data.pageInfo.dateTime}
              </Typography>
            </Grid>
            <Grid item xs={6} className="text-right">
              <Typography variant="body2" className="text-gray-600 text-xs">
                Page {data.pageInfo.currentPage} of {data.pageInfo.totalPages}
              </Typography>
              <Typography variant="body1" className="font-bold text-gray-800 text-sm">
                *** END OF STATEMENT ***
              </Typography>
            </Grid>
          </Grid>
        </Box>

        {/* Disclaimer - Compact */}
        <Box className="mt-2 p-2 bg-gray-50 rounded border-l-4 border-blue-600">
          <Typography variant="body2" className="text-gray-600 text-xs">
            <strong>Note:</strong> This is a computer-generated statement. For discrepancies, contact customer service
            within 30 days.
          </Typography>
        </Box>
      </Box>
    </Box>
  )
}

export default LoanStatement
